import { PollCountdown } from '@/components/PollCountDown'
import { formatMonthDayOrdinal } from '@/utils/datetime/formatDate'
import { match } from 'ts-pattern'
import { cn } from '@/lib/utils'
import { tv } from 'tailwind-variants'

export const labelStyles = tv({
	base: 'text-2xl leading-6 font-normal',
	variants: {
		pollType: {
			ended: 'bg-green text-neutral',
			ongoing: 'bg-green-40 text-neutral',
			upcoming: 'bg-green-10 text-neutral-40'
		}
	}
})

export function PollContainer({ children, className }: { children: React.ReactNode; className?: string }) {
	return <div className={cn('grid grid-rows-[auto_1fr_auto] pb-1 h-full w-full', className)}>{children}</div>
}

export function Header({ date, className }: { date: Date; className?: string }) {
	return <h3 className={cn('text-center text-6xl mb-6', className)}>{formatMonthDayOrdinal(date)}</h3>
}

export function QuestionBlock({
	question,
	pollType
}: {
	question: string
	pollType: 'ended' | 'ongoing' | 'upcoming'
}) {
	const label = match(pollType)
		.with('ended', () => 'Ended Poll')
		.with('ongoing', () => 'Ongoing Poll')
		.with('upcoming', () => 'Upcoming Poll')
		.exhaustive()

	return (
		<div className="grid grid-cols-1 relative">
			<span
				className={cn(
					'absolute top-1 left-[8%] -translate-y-1/2 text-neutral py-1 px-3 rounded-full leading-6 font-normal z-10',
					labelStyles({ pollType })
				)}
			>
				{label}
			</span>
			<img src="/images/poll-map/question.svg" alt="Poll Question" className="size-full col-[1/-1] row-[1/-1]" />
			<div className="col-[1/-1] row-[1/-1] p-6 pb-[calc(3%+1.5rem)] flex items-center justify-center">
				<span
					className="text-3xl uppercase overflow-hidden text-ellipsis line-clamp-3 text-center"
					title={question}
				>
					{question}
				</span>
			</div>
		</div>
	)
}

export function AnswerBlock({
	children,
	label
}: {
	children: React.ReactNode
	label: (props: { className: string }) => React.ReactNode
}) {
	return (
		<div className="grid grid-cols-1 relative min-h-[100px]">
			{label({
				className:
					'absolute bottom-0 right-[8%] translate-y-1/2 text-[24px] bg-green text-neutral py-1 px-3 rounded-full leading-6 font-normal z-10'
			})}
			<img src="/images/poll-map/answer.svg" alt="Poll Answer" className="size-full col-[1/-1] row-[1/-1]" />
			<div className="col-[1/-1] row-[1/-1] p-6 pt-[calc(2.5%+1.5rem)] flex items-center justify-center gap-3">
				{children}
			</div>
		</div>
	)
}

export function CountdownLabel({
	className,
	targetDate,
	onEnd,
	label
}: {
	className?: string
	targetDate: Date
	onEnd?: () => void
	label: string
}) {
	return (
		<div
			className={cn(
				'flex items-center gap-1 justify-center text-2xl whitespace-nowrap overflow-hidden',
				className
			)}
		>
			<span className="leading-6 overflow-hidden text-ellipsis flex-shrink min-w-0" title={label}>
				{label}:
			</span>
			<PollCountdown className="text-2xl leading-6 flex-shrink-0" endDate={targetDate} onEnd={onEnd} />
		</div>
	)
}
